import { useEffect, useState } from 'react'
import s from './AddUser.scss'
import { Button, Col, Row } from 'reactstrap'
import { KeyValuePairs, InputContainer, Spinner } from 'components/ideal-core-ui'
import Select from 'react-select'
import { getTenant, getCurrency, getUser, refreshToken } from 'utils/authService'
import { NumericFormat } from 'react-number-format'
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { getFormValidationError } from 'utils/formatting'
import queryString from 'query-string'
import { useImmer } from 'use-immer'
import { useDispatch } from 'react-redux'
import { toUsers } from '../serializers'

const initialState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  roles: [
    {
      label: 'Time Tracker Manager',
      description:
        'Can view and edit other people’s entries; can run reports including any/all Time Tracker users',
      value: 'ROLE_TIMETRACKER_ADMIN',
      checked: true
    },
    {
      label: 'Time Tracker User',
      description: 'Can only view and edit their own time entries; reports show only their time',
      value: 'ROLE_TIMETRACKER',
      checked: false
    }
  ],
  hoursPerWeek: '',
  standardRate: '',
  overtimeRate: '',
  secondShiftRate: '',
  thirdShiftRate: '',
  standardCost: '',
  overtimeCost: '',
  secondShiftCost: '',
  thirdShiftCost: ''
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Email is required!'),
  firstName: Yup.string().required('First Name field is required!'),
  lastName: Yup.string().required('Last Name field is required!'),
  password: Yup.string().required('Password field is required!')
})

const stripValue = value => {
  return String(value).replace(/[^\d.-]/g, '')
}

const AddUser = ({
  isEdit,
  organizationId,
  error,
  setError,
  iDealUsers,
  setTimeTrackerUsers,
  timeTrackerUsers,
  setIsEdit,
  setFormIsVisible,
  editedUserDetails,
  existingUser,
  selectedUser,
  setSelectedUser,
  setIdealUsers
}) => {
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useImmer(initialState)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [selectedExistingUser, setSelectedExistingUser] = useState(null)

  const loggedInUser = getUser()
  const users = toUsers(iDealUsers)

  if (selectedUser && selectedUser.value !== selectedExistingUser?.value) {
    setSelectedExistingUser(selectedUser)
  }

  useEffect(() => {
    setUserDetails(state => {
      state.organizationId = organizationId
    })
  }, [setUserDetails, userDetails.roles, organizationId])

  useEffect(() => {
    if (editedUserDetails) {
      setUserDetails(() => editedUserDetails)
    }
  }, [editedUserDetails, setUserDetails])

  const updateExistingUser = async () => {
    try {
      const localUser = users.find(user => user.value === editedUserDetails?.id)
      const response = await makePostRequest('/v1/timetracker/work-info', {
        user: {
          userId: isEdit ? editedUserDetails.id : selectedExistingUser.value,
          roles: [
            ...(isEdit
              ? localUser.roles.filter(role => !role.includes('ROLE_TIMETRACKER'))
              : selectedExistingUser.roles.filter(role => !role.includes('ROLE_TIMETRACKER'))),
            ...userDetails.roles.filter(role => role.checked).map(role => role.value)
          ]
        },
        hoursPerWeek: stripValue(userDetails.hoursPerWeek),
        standardRate: stripValue(userDetails.standardRate),
        overtimeRate: stripValue(userDetails.overtimeRate),
        secondShiftRate: stripValue(userDetails.secondShiftRate),
        thirdShiftRate: stripValue(userDetails.thirdShiftRate),
        standardCost: stripValue(userDetails.standardCost),
        overtimeCost: stripValue(userDetails.overtimeCost),
        secondShiftCost: stripValue(userDetails.secondShiftCost),
        thirdShiftCost: stripValue(userDetails.thirdShiftCost)
      })

      if (loggedInUser.id === response.user.userId) {
        refreshToken(() => {
          dispatch({
            type: 'REFRESH_HEADER'
          })
        })
      }

      if (isEdit) {
        const index = timeTrackerUsers.findIndex(user => user.id === editedUserDetails.id)
        setTimeTrackerUsers([
          ...timeTrackerUsers.slice(0, index),
          {
            userName: response.user.userName,
            email: response.user.userEmail,
            roles: response.user.roles,
            id: response.user.userId
          },
          ...timeTrackerUsers.slice(index + 1)
        ])
      } else {
        setTimeTrackerUsers([
          ...timeTrackerUsers,
          {
            userName: response.user.userName,
            email: response.user.userEmail,
            roles: response.user.roles,
            id: response.user.userId
          }
        ])
      }
      resetForm()
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'User updated successfuly',
          level: 'success'
        }
      })
    } catch (error) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'There was a problem updating user',
          level: 'error'
        }
      })
    }
  }

  const resetForm = () => {
    setIsEdit(false)
    setFormIsVisible(false)
    setError('')
    setSelectedExistingUser(null)
    setSelectedUser(null)
    setUserDetails(() => {
      return { ...initialState, organizationId }
    })
  }

  const addUser = () => {
    validationSchema
      .validate(userDetails)
      .then(async function () {
        setLoading(true)

        const { email } = userDetails

        const masterSchemaHeader = {
          'x-tenant-schema': 'master'
        }
        const emailExists = await makeGetRequest('/v1/tenants/email', {
          params: {
            email
          },
          headers: masterSchemaHeader
        })

        if (!emailExists) {
          // create tenant
          await makePostRequest(
            '/v1/tenants/create',
            {
              tenantId: getTenant(),
              username: email
            },
            {
              headers: masterSchemaHeader
            }
          )

          const { user } = await makePostRequest('/v1/users/signup', {
            email: userDetails.email,
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            organizationId: userDetails.organizationId,
            password: userDetails.password,
            roles: userDetails.roles.filter(role => role.checked).map(role => role.value)
          })
          await makePostRequest('/v1/timetracker/work-info', {
            user: {
              userId: user.id,
              roles: user.roles
            },
            hoursPerWeek: stripValue(userDetails.hoursPerWeek),
            standardRate: stripValue(userDetails.standardRate),
            overtimeRate: stripValue(userDetails.overtimeRate),
            secondShiftRate: stripValue(userDetails.secondShiftRate),
            thirdShiftRate: stripValue(userDetails.thirdShiftRate),
            standardCost: stripValue(userDetails.standardCost),
            overtimeCost: stripValue(userDetails.overtimeCost),
            secondShiftCost: stripValue(userDetails.secondShiftCost),
            thirdShiftCost: stripValue(userDetails.thirdShiftCost)
          })
          setFormIsVisible(false)
          setError('')
          setTimeTrackerUsers([...timeTrackerUsers, user])
          setIdealUsers([...iDealUsers, user])
          setUserDetails(() => {
            return { ...initialState, organizationId }
          })
          setLoading(false)
        } else {
          setError('Email already exists')
          setLoading(false)
        }
        const search = queryString.parse(location.search)
        if (search.stripe) {
          delete search.stripe
          navigate({ search: queryString.stringify(search) })
        }
      })
      .catch(function (err) {
        setError(getFormValidationError(err))
        setLoading(false)
      })
  }

  const changeProperty = (value, property) => {
    setUserDetails(state => {
      state[property] = value
    })
  }

  const changeRoles = index => {
    setUserDetails(state => {
      switch (index) {
        case 0:
          state.roles[0].checked = true
          state.roles[1].checked = false
          break
        case 1:
          state.roles[0].checked = false
          state.roles[1].checked = true
          break
      }
    })
  }

  return (
    <div className={s.formWrapper}>
      <Col xl="12" className={s.form}>
        {existingUser ? (
          <Select
            onChange={value => {
              setSelectedExistingUser(value)
            }}
            options={users}
            className={s.select}
            value={selectedExistingUser}
            isDisabled={isEdit}
          />
        ) : (
          !isEdit && (
            <Row>
              <Col lg="6">
                <KeyValuePairs
                  pairs={[
                    {
                      key: '* First Name',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="text"
                          placeholder="Enter First Name"
                          cb={value => changeProperty(value, 'firstName')}
                          text={userDetails.firstName}
                        />
                      )
                    },
                    {
                      key: '* Email',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="email"
                          placeholder="Enter Email"
                          cb={value => changeProperty(value, 'email')}
                          text={userDetails.email}
                          isDisabled={isEdit}
                        />
                      )
                    }
                  ]}
                />
              </Col>
              <Col lg="6">
                <KeyValuePairs
                  pairs={[
                    {
                      key: '* Last Name',
                      keyClass: [s.label],
                      valueClass: [s.value],
                      value: (
                        <InputContainer
                          type="text"
                          placeholder="Enter Last Name"
                          cb={value => changeProperty(value, 'lastName')}
                          text={userDetails.lastName}
                        />
                      )
                    },
                    !isEdit
                      ? {
                          key: '* Password',
                          keyClass: [s.label],
                          valueClass: [s.value],
                          value: (
                            <InputContainer
                              type="text"
                              placeholder="Enter Password"
                              cb={value => changeProperty(value, 'password')}
                              text={userDetails.password}
                            />
                          )
                        }
                      : {}
                  ]}
                />
              </Col>
            </Row>
          )
        )}
        {error && <p className={s.error}>{error}</p>}
        <p className={s.roles}>* Time Tracker Role </p>
        {userDetails.roles.map((role, index) => (
          <div key={role.value} className={s.inputContainer}>
            <input type="radio" checked={role.checked} onChange={() => changeRoles(index)} />{' '}
            <span className={s.roleLabel}>{role.label}</span>
            <p className={s.roleDescription}>{role.description}</p>
          </div>
        ))}
        <Row>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: '* Amount of working hours per week',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <InputContainer
                      type="number"
                      placeholder="Enter # of hours"
                      cb={value => changeProperty(value, 'hoursPerWeek')}
                      text={userDetails.hoursPerWeek}
                      classname={s.input}
                    />
                  )
                }
              ]}
            />
          </Col>
        </Row>
        <Row>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: 'Hourly Rate',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter hourly rate"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.standardCost}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'standardCost')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: 'Overtime rate',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Overtime rate"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.overtimeCost}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'overtimeCost')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: 'Second shift rate',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Second shift rate"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.secondShiftCost}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'secondShiftCost')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: 'Third shift rate',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Third shift rate"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.thirdShiftCost}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'thirdShiftCost')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                }
              ]}
            />
          </Col>
          <Col lg="6">
            <KeyValuePairs
              pairs={[
                {
                  key: '* Standard billable hour',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Standard billable hour"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.standardRate}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'standardRate')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: 'Overtime billable hour',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Overtime billable hour"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.overtimeRate}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'overtimeRate')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: 'Second shift billable hour',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Second shift billable hour"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.secondShiftRate}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'secondShiftRate')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                },
                {
                  key: 'Third shift billable hour',
                  keyClass: [s.label],
                  valueClass: [s.value],
                  value: (
                    <NumericFormat
                      thousandSeparator
                      placeholder="Enter Third shift billable hour"
                      decimalScale={2}
                      allowNegative={false}
                      value={userDetails.thirdShiftRate}
                      prefix={getCurrency().symbol}
                      onChange={formattedValue => {
                        changeProperty(formattedValue.target.value, 'thirdShiftRate')
                      }}
                      style={{
                        padding: '4px 10px',
                        width: 240
                      }}
                    />
                  )
                }
              ]}
            />
          </Col>
        </Row>
        {!isEdit &&
          (existingUser ? (
            <Button
              onClick={updateExistingUser}
              className={s.saveBtn}
              disabled={
                !selectedExistingUser || !userDetails.hoursPerWeek || !userDetails.standardRate
              }
            >
              {loading ? <Spinner /> : <>Update User</>}
            </Button>
          ) : (
            <Button
              onClick={addUser}
              className={s.saveBtn}
              disabled={!userDetails.hoursPerWeek || !userDetails.standardRate}
            >
              {loading ? <Spinner /> : <>Create User & Send Notification Email</>}
            </Button>
          ))}
        {isEdit && (
          <>
            <Button onClick={updateExistingUser} className={s.saveBtn}>
              {loading ? <Spinner /> : <>Save Changes</>}
            </Button>
            <Button onClick={resetForm}>Cancel</Button>
          </>
        )}
      </Col>
    </div>
  )
}

export default AddUser
